/**
 * Gym Shorts v4.1 — Pack selector behavior.
 *
 * Custom element <gs-v4-1-pack-selector>:
 *   - Click on a [data-pack] tile selects that pack.
 *   - Dispatches the SAME gs-v4:pack-change event v4 uses, so the design picker
 *     (assets/gym-shorts-v4.js) reacts to budget changes unchanged.
 *   - Updates the Showtime headline price block for the selected pack. Multi-
 *     packs lead with the PER-ITEM price ("$35.00 each") so the big number is
 *     never mistaken for a single-shorts price; the pack total + compare move
 *     to a smaller "Total $69.99 for 2 shorts $79.98" line right below.
 *   - Rescales the Shop Pay installments widget (<shopify-payment-terms>) to
 *     the pack total — Shopify server-renders it against the base variant
 *     price, which is wrong the moment a multi-pack is selected.
 *
 * No coordinator logic here — gs-v4-coordinator from gym-shorts-v4.js handles
 * cart submission for both v4 and v4.1.
 */
(() => {
  const REG_SEL = '.\\#price-item.\\@regular';
  const CMP_SEL = '.\\#price-item.\\@compare';

  class GsV41PackSelector extends HTMLElement {
    connectedCallback() {
      this._onClick = (e) => {
        const tile = e.target.closest('[data-pack]');
        if (!tile || !this.contains(tile)) return;
        const n = Number(tile.dataset.pack);
        if (!Number.isFinite(n)) return;
        if (n === this.selected) return;
        this.select(n);
      };
      this.addEventListener('click', this._onClick);
      // Sync headline price to the default pack on first paint.
      const initial = this.querySelector(`[data-pack="${this.selected}"]`);
      if (initial) this._applyHeadline(initial);
    }
    disconnectedCallback() {
      if (this._onClick) this.removeEventListener('click', this._onClick);
    }
    /** Exposed for gs-v4-coordinator (matches v4 pack-selector contract). */
    get selected() { return Number(this.dataset.selectedPack); }

    select(n) {
      this.dataset.selectedPack = String(n);
      let chosen = null;
      this.querySelectorAll('[data-pack]').forEach((t) => {
        const active = Number(t.dataset.pack) === n;
        t.classList.toggle('is-selected', active);
        t.setAttribute('aria-checked', String(active));
        if (active) chosen = t;
      });
      if (chosen) this._applyHeadline(chosen);
      document.dispatchEvent(
        new CustomEvent('gs-v4:pack-change', { detail: { pack: n } })
      );
    }

    _applyHeadline(tile) {
      const n = Number(tile.dataset.pack) || 1;
      const price = tile.dataset.packPrice || '';
      const compare = tile.dataset.packCompare || '';
      const priceCents = Number(tile.dataset.packPriceCents || 0);
      const compareCents = Number(tile.dataset.packCompareCents || 0);
      const hasCompare = compareCents > priceCents && compareCents > 0;
      const multi = n > 1 && priceCents > 0;

      const reg = document.querySelector(REG_SEL);
      if (!reg) return;

      // Reuse the store's money format (prefix/suffix) from the pack price.
      const fm = price.match(/^(\D*)[\d.,]+(\D*)$/);
      const fmt = (c) => (fm ? fm[1] : '$') + (c / 100).toFixed(2) + (fm ? fm[2] : '');
      const labelEl = tile.querySelector('.gs-v41-pack__label');
      const packLabel = labelEl ? labelEl.textContent.trim() : `${n} shorts`;

      // Headline: per-item price for multi-packs, plain price for singles.
      if (multi) {
        reg.textContent = '';
        reg.append(fmt(Math.round(priceCents / n)), ' ');
        const each = document.createElement('span');
        each.className = 'gs-v41-price-each';
        each.textContent = 'each';
        reg.append(each);
      } else {
        reg.textContent = price;
      }

      // "Total $69.99 for 2 shorts $79.98" line under the headline.
      let total = reg.parentNode.querySelector('.gs-v41-price-total');
      if (!total) {
        total = document.createElement('div');
        total.className = 'gs-v41-price-total';
        reg.parentNode.insertBefore(total, reg.nextSibling);
      }
      total.textContent = '';
      if (multi) {
        total.append('Total ');
        const strong = document.createElement('strong');
        strong.textContent = price;
        total.append(strong, ` for ${packLabel}`);
        if (hasCompare) {
          const s = document.createElement('s');
          s.className = 'gs-v41-price-total__compare';
          s.textContent = compare;
          total.append(' ', s);
        }
        total.removeAttribute('hidden');
      } else {
        total.setAttribute('hidden', '');
      }

      // Inline compare <s> next to the headline — single-pack view only
      // (multi-packs show the compare inside the total line instead).
      const wantInline = !multi && hasCompare;
      let cmp = document.querySelector(CMP_SEL);
      if (!cmp && wantInline) {
        cmp = document.createElement('s');
        cmp.setAttribute('class', '#price-item @compare');
        reg.parentNode.insertBefore(cmp, total.nextSibling);
      }
      if (cmp) {
        if (wantInline) {
          cmp.textContent = compare;
          cmp.removeAttribute('hidden');
          cmp.style.display = '';
          cmp.style.textDecoration = 'line-through';
        } else {
          cmp.textContent = '';
          cmp.setAttribute('hidden', '');
          cmp.style.display = 'none';
        }
      }

      this._updateInstallments(multi ? priceCents : 0, fmt);
    }

    /**
     * Rescale <shopify-payment-terms> to the pack total. Shopify renders its
     * shopify-meta JSON against the base variant price ($39.99), so the
     * "Pay in N interest-free installments of $X" line is wrong for packs.
     * The installment count is NOT fixed — Shopify's financing_plans (embedded
     * in the same meta) tier it by amount (e.g. $35–$49.99 → 2 payments,
     * $50–$149.99 → 4), so both the count and the per-term amount are
     * recomputed for the pack total. The component observes shopify-meta
     * (attributeChangedCallback), so setting the attribute repaints it.
     * totalCents = 0 restores the original server-rendered meta (single pack).
     */
    _updateInstallments(totalCents, fmt) {
      const el = document.querySelector('shopify-payment-terms');
      if (!el) return;
      if (this._origTermsMeta === undefined) {
        this._origTermsMeta = el.getAttribute('shopify-meta') || '';
      }
      if (!this._origTermsMeta) return;
      let next = this._origTermsMeta;
      if (totalCents > 0) {
        let meta;
        try { meta = JSON.parse(this._origTermsMeta); } catch { return; }
        if (!Array.isArray(meta.variants)) return;
        const terms = this._installmentsCount(meta, totalCents);
        meta.variants.forEach((v) => {
          v.full_price = fmt(totalCents);
          v.number_of_payment_terms = terms;
          v.price_per_term = fmt(Math.round(totalCents / terms));
        });
        next = JSON.stringify(meta);
      }
      if (el.getAttribute('shopify-meta') !== next) {
        el.setAttribute('shopify-meta', next);
      }
    }

    /**
     * Zero-APR installment count for a given total, resolved from the
     * financing_plans tiers in shopify-meta. Falls back to the variant's
     * server-rendered count if no tier matches (e.g. total below Shop Pay's
     * minimum). Money strings ("$1,234.56") are compared as digit-only cents.
     */
    _installmentsCount(meta, totalCents) {
      const cents = (s) => Number(String(s || '').replace(/\D/g, '')) || 0;
      const plans = Array.isArray(meta.financing_plans) ? meta.financing_plans : [];
      for (const plan of plans) {
        if (totalCents < cents(plan.min_price)) continue;
        if (totalCents > cents(plan.max_price)) continue;
        const t = (plan.terms || []).find((x) => x.apr === 0) || (plan.terms || [])[0];
        if (t && t.installments_count) return Number(t.installments_count);
      }
      const v = meta.variants[0];
      return Number(v && v.number_of_payment_terms) || 4;
    }
  }

  if (!customElements.get('gs-v4-1-pack-selector')) {
    customElements.define('gs-v4-1-pack-selector', GsV41PackSelector);
  }
})();
